import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import { SlideshowLightbox, initLightboxJS } from "lightbox.js-react";
import { PageHeadline } from "../components/PageHeadline";
import { Button, Headline, Paragraph, Icon, Container } from "../components/ui";
import { ContactForm } from "../components/ContactForm";
import CTAWhatsApp from "../components/CTAWhatsApp";
import HighlightBar from "../components/HighlightBar";
import { api, transformInserat } from "../services/api";

const OfferBasics = ({ inserat }) => {
  if (!inserat) return null;

  const details = [
    { label: "Modell", value: inserat.model },
    { label: "Baujahr", value: inserat.year },
    { label: "Laufleistung", value: `${inserat.mileage.toLocaleString()} km` },
    { label: "Motor", value: inserat.engine },
    { label: "Getriebe", value: inserat.transmission },
  ];

  return (
    <div className="my-4 sm:my-8">
      <div
        className="grid grid-cols-2 gap-4 sm:gap-6 mb-8"
        style={{ maxWidth: 600 }}
      >
        {details.map((detail, index) => (
          <div key={index} className="space-y-2 sm:space-y-3">
            <Paragraph
              variant="tiny"
              color="secondary"
              className="font-medium  mb-[-8px] "
            >
              {detail.label}
            </Paragraph>
            <Paragraph
              variant="medium"
              color="secondary"
              className="font-medium"
            >
              {detail.value}
            </Paragraph>
          </div>
        ))}
      </div>
      {inserat.price > 0 && (
        <Headline
          variant="splitscreen-heading"
          color="secondary"
          className="text-left mb-8 mt-[-10px]"
        >
          {inserat.price.toLocaleString()} €
        </Headline>
      )}
    </div>
  );
};

const OfferOverview = ({ inserat }) => {
  if (!inserat || !inserat.overview) return null;

  // Convert rich text to plain text and parse key-value pairs
  const overviewText =
    typeof inserat.overview === "string" ? inserat.overview : "";

  // Parse lines that contain "key: value" format
  const overviewItems = overviewText
    .split("\n")
    .filter((line) => line.trim().length > 0)
    .map((line) => {
      const trimmedLine = line.trim();
      // Remove leading dash if present
      const cleanLine = trimmedLine.startsWith("- ")
        ? trimmedLine.substring(2)
        : trimmedLine;

      // Split on first colon to separate key and value
      const colonIndex = cleanLine.indexOf(":");
      if (colonIndex > 0) {
        const key = cleanLine.substring(0, colonIndex).trim();
        const value = cleanLine.substring(colonIndex + 1).trim();
        return { key, value };
      }

      // If no colon found, treat entire line as value with empty key
      return { key: "", value: cleanLine };
    })
    .filter((item) => item.key || item.value); // Filter out empty items

  if (overviewItems.length === 0) return null;

  return (
    <div className="pt-8 sm:pt-12 mb-6 sm:mb-12">
      <Headline variant="h3Offer" color="primary" className="pt-0 text-left">
        Überblick
      </Headline>
      <div className="space-y-4">
        {overviewItems.map((item, index) => (
          <div
            key={index}
            className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-1 sm:gap-8 mt-8 p-4 sm:p-8 rounded-sm bg-white/70"
          >
            {item.key && (
              <div className="font-semibold">
                <Paragraph
                  variant="medium"
                  color="primary"
                  className="font-bold"
                  noSpacing={true}
                >
                  {item.key}
                </Paragraph>
              </div>
            )}
            <div className={item.key ? "" : "sm:col-span-2"}>
              <Paragraph variant="medium" color="primary" noSpacing={true}>
                {item.value}
              </Paragraph>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OfferHighlights = ({ inserat }) => {
  if (!inserat || !inserat.highlights) return null;

  // Convert rich text to plain text and split into features
  const highlightsText =
    typeof inserat.highlights === "string" ? inserat.highlights : "";
  const features = highlightsText
    .split("\n")
    .filter((line) => line.trim().length > 0);

  return (
    <div>
      <Headline
        variant="h3Offer"
        color="primary"
        className="pt-0 text-left"
        animate={false}
      >
        Highlights
      </Headline>
      <div className="space-y-4">
        {features.map((feature, index) => (
          <div key={index} className="flex items-start gap-3">
            <Icon
              name="star"
              size="sm"
              className="mt-1 flex-shrink-0"
              style={{ color: "var(--color-highlight-yellow)" }}
            />
            <Paragraph variant="medium" color="primary">
              {feature.trim()}
            </Paragraph>
          </div>
        ))}
      </div>
    </div>
  );
};

const OfferDescription = ({ inserat }) => {
  if (!inserat || !inserat.description) return null;

  return (
    <div className="mb-6 sm:mb-12 pt-12">
      <Headline variant="h3Offer" color="primary" className="pt-0 text-left">
        Beschreibung
      </Headline>
      <Paragraph variant="medium" color="primary" className="leading-relaxed">
        {inserat.description}
      </Paragraph>
    </div>
  );
};

const ImagePlaceholder = ({ className }) => (
  <div
    className={`${className} bg-gray-200 relative overflow-hidden min-h-[8rem]`}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer bg-[length:200%_100%]" />
  </div>
);

const OfferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inserat, setInserat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const fetchInserat = async () => {
      try {
        setLoading(true);
        const strapiInserat = await api.getInserat(id);
        if (strapiInserat) {
          const transformedInserat = transformInserat(strapiInserat);
          setInserat(transformedInserat);
        } else {
          setError("Angebot nicht gefunden");
        }
      } catch (err) {
        console.error("Error fetching inserat:", err);
        setError("Fehler beim Laden des Angebots");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInserat();
    }
  }, [id]);

  // Wait for images to actually load
  useEffect(() => {
    if (
      inserat &&
      inserat.images &&
      inserat.images.length > 0 &&
      !imagesLoaded
    ) {
      const imagePromises = inserat.images.slice(0, 3).map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Still resolve on error
          img.src = src;
        });
      });

      Promise.all(imagePromises).then(() => {
        setImagesLoaded(true);
        // Initialize lightbox after images are loaded
        requestAnimationFrame(() => {
          initLightboxJS("ch_3QMdj8JAthSyOIuA0GSfVViL", "individual");
        });
      });
    }
  }, [inserat, imagesLoaded]);

  const handleMoreImagesClick = () => {
    // Find and click on the 4th image (first hidden one) to open lightbox at that position
    const hiddenImage = document.querySelector(".lightbox-grid img.hidden");
    if (hiddenImage) {
      hiddenImage.click();
    }
  };

  const handleBackClick = () => {
    // Navigate back to the previous page in history
    navigate(-1);
  };

  // Generate dynamic SEO data based on vehicle details
  const generateSEOData = () => {
    if (!inserat) {
      return {
        title: "Fahrzeug Details | WTM Sportwagen",
        description:
          "Fahrzeug Details bei WTM Sportwagen - Ihrem Porsche Spezialisten in Gießen.",
        keywords: "Porsche kaufen, WTM Sportwagen, Gießen",
      };
    }

    const { title, model, year, mileage, price, engine } = inserat;

    // Create SEO-optimized title
    const seoTitle = `${title} kaufen | ${year} ${model} - WTM Sportwagen`;

    // Create detailed description
    const seoDescription = `${title} zu verkaufen bei WTM Sportwagen. Baujahr ${year}, ${mileage.toLocaleString()} km, ${
      engine || "Porsche Motor"
    }.${
      price > 0 ? ` Preis: ${price.toLocaleString()}€.` : ""
    } Geprüfte Qualität aus Gießen.`;

    // Generate keywords
    const keywords = [
      title,
      `${model} kaufen`,
      `Porsche ${year}`,
      `${model} ${year}`,
      "WTM Sportwagen",
      "Porsche kaufen Gießen",
      "Sportwagen kaufen",
    ].join(", ");

    return {
      title: seoTitle,
      description: seoDescription,
      keywords,
      price: price > 0 ? price.toLocaleString() : "0",
      year,
      model,
      mileage: mileage.toLocaleString(),
    };
  };

  const seoData = generateSEOData();

  if (!loading && (error || !inserat)) {
    return (
      <div
        style={{ backgroundColor: "var(--color-primary)", minHeight: "100vh" }}
      >
        <Container maxWidth="4xl" className="py-8">
          <div className="text-center">
            <Paragraph
              variant="medium"
              className="mb-6"
              style={{ color: "var(--color-highlight)" }}
            >
              {error || "Angebot nicht gefunden"}
            </Paragraph>
            <Button onClick={handleBackClick} variant="outline" size="medium">
              Zurück zu den Angeboten
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (!inserat) {
    return null; // Don't render anything while loading
  }

  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />

        {/* Open Graph tags for vehicle listing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content={`https://wtm-sportwagen.com/angebot/${id}`}
        />
        {inserat?.images?.[0] ? (
          <meta property="og:image" content={inserat.images[0]} />
        ) : null}

        {/* Product specific meta tags */}
        <meta property="product:price:amount" content={seoData.price || "0"} />
        <meta property="product:price:currency" content="EUR" />
        <meta property="product:condition" content="used" />
        <meta property="product:brand" content="Porsche" />
        <meta property="product:category" content="Automobiles" />
        <meta property="product:availability" content="in stock" />

        {/* Twitter Card for vehicle */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        {inserat?.images?.[0] ? (
          <meta name="twitter:image" content={inserat.images[0]} />
        ) : null}

        {/* Structured data hints */}
        <meta name="vehicle:make" content="Porsche" />
        <meta name="vehicle:model" content={seoData.model || "Porsche"} />
        <meta name="vehicle:year" content={seoData.year || "0"} />
        <meta name="vehicle:mileage" content={seoData.mileage || "0"} />

        {/* Canonical URL */}
        <link
          rel="canonical"
          href={`https://wtm-sportwagen.com/angebot/${id}`}
        />
      </Helmet>

      <div
        style={{
          backgroundColor: "var(--color-primary)",
          minHeight: "100vh",
        }}
      >
        <Container
          maxWidth="none"
          className="py-4 §sm:py-16 px-0 sm:px-6 lg:px-8 sm:mb-16 mb-0"
        >
          {/* Two-column layout on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-8 lg:gap-12 mb-8 sm:pt-4">
            {/* Left column: Slideshow */}
            <div className="order-1 lg:order-1 -mx-4 sm:-mx-6 lg:mx-0 relative">
              {/* Back Button - above slideshow on desktop, over slideshow on mobile */}
              <div className="absolute top-4 left-4 z-10 lg:relative lg:top-auto lg:left-auto lg:mb-4 lg:z-auto">
                <Button
                  onClick={handleBackClick}
                  variant="primary"
                  size="small"
                  className="shadow-lg"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                  Zurück
                </Button>
              </div>
              {inserat.images && inserat.images.length > 0 && (
                <div className="lightbox-grid grid grid-cols-2 gap-1 sm:gap-2">
                  {!imagesLoaded ? (
                    // Show placeholders while images load - 3 on mobile, 5 on desktop
                    Array.from({ length: 5 }, (_, index) => (
                      <ImagePlaceholder
                        key={`placeholder-${index}`}
                        className={`w-full rounded-sm object-cover aspect-[16/11] ${
                          index === 0 ? "col-span-2" : ""
                        } ${index === 3 ? "hidden sm:block" : ""} ${
                          index === 4 ? "hidden sm:block" : ""
                        }`}
                      />
                    ))
                  ) : (
                    <>
                      {/* Show actual slideshow when images are loaded */}
                      <SlideshowLightbox
                        className="contents"
                        showThumbnails={true}
                      >
                        {inserat.images.map((src, index) => (
                          <img
                            key={index}
                            className={`w-full rounded-sm cursor-pointer hover:opacity-90 transition-opacity object-cover aspect-[16/11] ${
                              index === 0 ? "col-span-2" : ""
                            } ${index >= 4 ? "hidden" : ""} ${
                              index === 3 ? "hidden sm:block" : ""
                            }`}
                            src={src}
                            alt={`${inserat.title} - Bild ${index + 1}`}
                          />
                        ))}
                      </SlideshowLightbox>

                      {/* 5th slot - Grey card with remaining count - Hidden on mobile */}
                      {inserat.images.length > 4 && (
                        <div
                          className="hidden sm:flex aspect-[16/11] bg-gray-800 rounded-sm flex-col items-center justify-center cursor-pointer transition-all duration-300 hover:bg-gray-700"
                          onClick={() => {
                            // Click on the 5th image (index 4) to open lightbox at that position
                            const hiddenImages = document.querySelectorAll(
                              ".lightbox-grid img.hidden"
                            );
                            if (hiddenImages[0]) {
                              hiddenImages[0].click();
                            }
                          }}
                        >
                          <div className="text-white text-center">
                            <div className="text-3xl font-bold mb-2">
                              {inserat.images.length - 4}
                            </div>
                            <div className="text-sm">
                              weitere Bilder anzeigen
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Show "more images" indicator if there are more than 3 images */}
              {/* {inserat.images && inserat.images.length > 3 && (
                <div className="mt-4 text-center lg:text-left px-8 lg:px-0">
                  <Button
                    onClick={handleMoreImagesClick}
                    variant="ghostSecondary"
                    size="small"
                  >
                    +{inserat.images.length - 3} weitere Bilder in der Galerie
                  </Button>
                </div>
              )} */}
            </div>

            {/* Right column: Title and Basics */}
            <div className="order-2 lg:order-2 lg:sticky lg:mt-16 lg:top-24 lg:self-start">
              <Headline
                variant="h2"
                color="secondary"
                className="text-left uppercase leading-tight mt-[-10px] "
              >
                {inserat.title}
              </Headline>

              <OfferBasics inserat={inserat} />
            </div>
          </div>
        </Container>

        <div
          className="pt-6 sm:pt-12 pb-6 sm:pb-12 "
          style={{ backgroundColor: "var(--color-secondary)" }}
        >
          {/* HighlightBar overlapping with black section above */}
          <div className="relative -mt-16 sm:-mt-24 mb-8">
            <HighlightBar
              title="HIGHLIGHTS"
              items={
                inserat.highlights
                  ? inserat.highlights
                      .split("\n")
                      .map((line) => line.trim())
                      .filter((line) => line.length > 0)
                  : [
                      "Sehr gepflegt",
                      "Varioram",
                      "Original",
                      "Scheckheftgepflegt",
                      "Unfallfrei",
                      "Vollausstattung",
                    ]
              }
              backgroundColor="var(--color-secondary-dark)"
              textColor="var(--color-primary)"
            />
          </div>
          <div className="max-w-4xl mx-auto px-4">
            <OfferDescription inserat={inserat} />
            <OfferOverview inserat={inserat} />

            {/* <OfferHighlights inserat={inserat} /> */}
          </div>
        </div>

        {/* WhatsApp CTA Section */}
        {/* <div className="mt-12 pt-8 border-t border-gray-300">
            <CTAWhatsApp
              firstParagraph="Interesse an diesem Fahrzeug?"
              secondParagraph="Kontaktieren Sie uns direkt via WhatsApp"
              buttonText="WhatsApp Nachricht"
            />
          </div> */}
        {/* Contact Buttons - positioned above contact form */}
        <div className="bg-[var(--color-primary)] py-6 pt-14">
          <div className="flex flex-col sm:flex-row gap-4 max-w-4xl mx-auto px-6">
            <Button
              variant="primary"
              size="large"
              className="flex-1 font-medium py-4 px-6 rounded-full"
              onClick={() => (window.location.href = "/kontakt")}
            >
              Kontakt aufnehmen
            </Button>

            <Button
              variant="primary"
              size="large"
              className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-4 px-6 rounded-full flex items-center justify-center gap-2"
              onClick={() =>
                window.open(
                  "https://wa.me/491573434888?text=Hallo,%20ich%20interessiere%20mich%20für%20das%20Fahrzeug%20" +
                    encodeURIComponent(inserat.model),
                  "_blank"
                )
              }
            >
              WhatsApp
            </Button>
          </div>
        </div>

        {/* Contact Form Section */}
        <div>
          <ContactForm
            headline="Interesse an diesem Fahrzeug?"
            intro="Kontaktieren Sie uns für weitere Informationen oder eine Besichtigung."
            formHeadline="Nachricht senden"
            formIntro={`Bezug: ${inserat.title}`}
            hideMessage={true}
          />
        </div>
      </div>
    </>
  );
};

export default OfferDetail;
